import { ApplicationRef, EnvironmentInjector } from '@angular/core';
import {
  createDockview,
  DockviewApi,
  IDockviewPanel,
  themeAbyss,
  AddPanelOptions,
  SerializedDockview,
} from 'dockview-core';
import { DockviewIDisposable } from 'dockview-core';
import { WindowManager, PanelDescriptor, SerializedLayout, PanelPosition, PanelKind } from './window-manager';
import { AngularPanelRenderer } from './angular-panel-renderer';

/**
 * WindowManager implementation backed by dockview-core.
 *
 * Provides VS Code-style tabbed/split pane layout. Each panel gets an
 * AngularPanelRenderer that bridges dockview's vanilla DOM with Angular
 * components via createComponent + ApplicationRef.attachView.
 */
export class DockviewWindowManager extends WindowManager {
  private _api: DockviewApi | null = null;
  private _disposables: DockviewIDisposable[] = [];
  /** Home group per kind: where the last panel of that kind was opened or
   *  dragged to. Only the initial placement uses it — a panel the user moved
   *  is never re-homed, it just makes its new group the home. */
  private _homeGroup = new Map<PanelKind, string>();
  private _renderers = new Map<string, AngularPanelRenderer>();
  private _layoutStorageKey: string | null = null;
  private _suppressPersist = false;
  private _layoutChangeDebounce: any = null;

  private static readonly _WIDGET_DISPLAY_NAMES: Record<string, string> = {
    'ChatWidget': 'Work Sessions',
    'HeartbeatWidget': 'Workspace',
    'SettingsWidget': 'Settings',
  };

  /** A workspace tab wears its team's name (2026-10-26). The grid page knows the
   *  teams, so it hands the resolver in; without one the tab stays "Workspace". */
  public workspaceTitleFor: ((teamSlug: string | null) => string) | null = null;

  /** Re-title every open workspace tab (e.g. once the team list has loaded and the
   *  slug can become the display name). Keeps the badge base in step. */
  retitleWorkspaces(): void {
    if (!this._api || !this.workspaceTitleFor) return;
    for (const panel of this._api.panels) {
      if ((panel as any).view?.contentComponent !== 'HeartbeatWidget') continue;
      const slug = (panel as any).params?.inputs?.teamName ?? null;
      const title = this.workspaceTitleFor(slug);
      const current = this._baseTitles.get(panel.id) ?? DockviewWindowManager._stripBadge(panel.title || '');
      if (title && title !== current) {
        this._baseTitles.set(panel.id, title);
        const badge = /\((\d+\+?)\)$/.exec(panel.title || '');
        panel.api.setTitle(badge ? `${title} (${badge[1]})` : title);
      }
    }
  }

  constructor(
    private _injector: EnvironmentInjector,
    private _appRef: ApplicationRef
  ) {
    super();
  }

  /** IDs of all current dockview groups */
  get groupIds(): string[] {
    return this._api ? this._api.groups.map(g => g.id) : [];
  }

  /** Set the localStorage key for persisting the dockview layout JSON */
  setLayoutStorageKey(key: string): void {
    this._layoutStorageKey = key;
  }

  init(container: HTMLElement): void {
    this._api = createDockview(container, {
      theme: themeAbyss,
      createComponent: (options) => {
        const renderer = new AngularPanelRenderer(this._injector, this._appRef);
        // Track renderer by panel ID so we can re-wire outputs after layout restore
        if (options.id) {
          this._renderers.set(options.id, renderer);
        }
        return renderer;
      },
    });

    // Wire dockview events → PanelEvent stream
    this._disposables.push(
      this._api.onDidRemovePanel((panel: IDockviewPanel) => {
        this._panelEvent$.next({ type: 'close', panelId: panel.id });
        this._renderers.delete(panel.id);
        this._baseTitles.delete(panel.id);
        queueMicrotask(() => {
          this._updateSingleTabHeaders();
          this._persistLayout();
        });
      })
    );

    this._disposables.push(
      this._api.onDidAddPanel((_panel: IDockviewPanel) => {
        queueMicrotask(() => {
          this._updateSingleTabHeaders();
          this._persistLayout();
        });
      })
    );

    this._disposables.push(
      this._api.onDidActivePanelChange((panel: IDockviewPanel | undefined) => {
        if (panel) {
          this._panelEvent$.next({ type: 'focus', panelId: panel.id });
        }
      })
    );

    // When tabs are dragged between groups, panel counts change and layout changes
    this._disposables.push(
      this._api.onDidMovePanel((e) => {
        if (e.panel?.group) this._homeGroup.set(this._kindOf(e.panel.params as any), e.panel.group.id);
        queueMicrotask(() => {
          this._updateSingleTabHeaders();
          this._persistLayout();
        });
      })
    );

    // Group lifecycle: closing the last panel OF A GROUP tears the group down
    // after onDidRemovePanel has fired, so the single-tab update there can run
    // against a groups list that still counts the dying group — leaving an
    // empty header strip above the surviving panel. Re-run on group changes.
    this._disposables.push(
      this._api.onDidAddGroup(() => {
        queueMicrotask(() => this._updateSingleTabHeaders());
      })
    );
    this._disposables.push(
      this._api.onDidRemoveGroup(() => {
        queueMicrotask(() => this._updateSingleTabHeaders());
      })
    );

    // Persist on any layout change (including split-divider drags). The
    // debounced pass also re-checks headers — the catch-all for any removal
    // ordering the events above miss.
    this._disposables.push(
      this._api.onDidLayoutChange(() => {
        clearTimeout(this._layoutChangeDebounce);
        this._layoutChangeDebounce = setTimeout(() => {
          this._updateSingleTabHeaders();
          this._persistLayout();
        }, 200);
      })
    );
  }

  dispose(): void {
    clearTimeout(this._layoutChangeDebounce);
    this._disposables.forEach(d => d.dispose());
    this._disposables = [];
    this._renderers.clear();
    this._api?.dispose();
    this._api = null;
  }

  /** Re-wire outputs on a panel's renderer (used after layout restore) */
  wireOutputs(panelId: string, outputs: Record<string, any>): void {
    const renderer = this._renderers.get(panelId);
    if (renderer) {
      renderer.wireOutputs(outputs);
    }
  }

  addPanel(descriptor: PanelDescriptor): string {
    if (!this._api) {
      throw new Error('[DockviewWindowManager] Not initialized — call init() first');
    }

    const options: AddPanelOptions = {
      id: descriptor.id,
      component: descriptor.component,
      title: descriptor.title || descriptor.component,
      params: descriptor as any,
      // Session widgets and team/system apps use the 'always' renderer so their
      // DOM stays in a hidden container when another tab is active: chat keeps
      // its scroll position, and an app's web component is never disconnected
      // and re-created — with the default renderer, switching tabs and back
      // re-ran the Team Chat app's connectedCallback and dropped the user into
      // its first channel (Ben, 2026-09-24).
      renderer: this._keepsDomWhenHidden(descriptor.component) ? 'always' : undefined,
    };

    // Position: explicit position from descriptor takes priority; otherwise
    // the kind's placement policy (see PanelKind).
    const kind = this._kindOf(descriptor);
    const home = this._homeGroup.get(kind);
    const wide = window.innerWidth >= 768;
    if (descriptor.position) {
      const pos = this._mapPosition(descriptor.position);
      if (pos) {
        (options as any).position = pos;
      }
    } else if (home && this._api.getGroup(home)) {
      // Like things are sibling tabs of the last group of their kind
      (options as any).position = { referenceGroup: home };
    } else if (kind === 'terminal' && wide) {
      // First terminal: a drawer across the whole width at the bottom
      (options as any).position = { direction: 'below' };
    } else if (this._api.activePanel && kind !== 'session' && wide) {
      // First document/tool/app: split right of the active panel so it docks
      // beside the current view instead of replacing it.
      (options as any).position = {
        referencePanel: this._api.activePanel.id,
        direction: 'right',
      };
    } else if (this._api.activeGroup) {
      // Sessions (or mobile, or nothing active) join the active group as a tab.
      (options as any).position = { referenceGroup: this._api.activeGroup.id };
    }

    const panel = this._api.addPanel(options);

    this._homeGroup.set(kind, panel.group.id);

    return panel.id;
  }

  removePanel(id: string): void {
    if (!this._api) return;
    const panel = this._api.getPanel(id);
    if (panel) {
      this._api.removePanel(panel);
    }
  }

  hasPanel(id: string): boolean {
    if (!this._api) return false;
    return !!this._api.getPanel(id);
  }

  focusPanel(id: string): void {
    if (!this._api) return;
    const panel = this._api.getPanel(id);
    if (panel) {
      panel.api.setActive();
    }
  }

  private _baseTitles = new Map<string, string>();

  /** A tab title without any trailing badge(s): "Team Chat (1) (1)" → "Team Chat". */
  private static _stripBadge(title: string): string {
    return title.replace(/(\s\((?:\d+|99\+)\))+$/, '');
  }

  setPanelBadge(id: string, count: number | null): void {
    if (!this._api) return;
    const panel = this._api.getPanel(id);
    if (!panel) return;
    // The badged title is what gets saved in the layout, so after a reload the
    // panel's title already carries "(1)" — taking it verbatim as the base
    // stacked a new "(1)" on every reload (Dyke, 2026-09-24: "Team Chat (1) (1) (1)").
    if (!this._baseTitles.has(id)) {
      this._baseTitles.set(id, DockviewWindowManager._stripBadge(panel.title || id));
    }
    const base = this._baseTitles.get(id)!;
    const n = Math.max(0, Math.floor(Number(count) || 0));
    panel.api.setTitle(n > 0 ? `${base} (${n > 99 ? '99+' : n})` : base);
  }

  getLayout(): SerializedLayout {
    if (!this._api) {
      return { type: 'dockview', version: 1, data: null };
    }
    return {
      type: 'dockview',
      version: 1,
      data: this._api.toJSON(),
    };
  }

  restoreLayout(layout: SerializedLayout): void {
    if (!this._api) return;
    if (layout.type !== 'dockview') {
      throw new Error('Cannot restore non-dockview layout');
    }
    // Patch saved panels: fix renderer for session widgets, update display names,
    // and strip one-shot values that should not survive a page reload.
    const data = layout.data as SerializedDockview;
    if (data?.panels) {
      for (const [, panel] of Object.entries(data.panels)) {
        const p = panel as any;
        if (this._keepsDomWhenHidden(p.contentComponent)) {
          p.renderer = 'always';
        }
        // Update display names for renamed widgets; a workspace tab takes its team's name
        if (p.contentComponent === 'HeartbeatWidget' && this.workspaceTitleFor) {
          p.title = this.workspaceTitleFor(p.params?.inputs?.teamName ?? null);
        } else {
          const displayName = DockviewWindowManager._WIDGET_DISPLAY_NAMES[p.contentComponent];
          if (displayName && p.title) {
            p.title = displayName;
          }
        }
        // Badges are live state, not layout: never restore a "(n)" suffix.
        if (typeof p.title === 'string') {
          p.title = DockviewWindowManager._stripBadge(p.title);
        }
        // Strip one-shot work session warm-up to prevent re-firing on reload
        if (p.params?.inputs?.workSessionWarmUp) {
          delete p.params.inputs.workSessionWarmUp;
        }
      }
    }
    // Suppress layout persistence during restore (fromJSON fires onDidAddPanel for each panel)
    this._suppressPersist = true;
    try {
      this._api.fromJSON(data);
      this._updateSingleTabHeaders();
      this._seedHomeGroups();
    } finally {
      this._suppressPersist = false;
    }
  }

  /** Get all panel entries (id + component name) currently in dockview */
  get allPanels(): { id: string; component: string }[] {
    if (!this._api) return [];
    return this._api.panels.map(p => ({
      id: p.id,
      component: (p as any).view?.contentComponent ?? ''
    }));
  }

  /** Wire outputs for every panel currently in dockview, using a resolver function */
  wireAllOutputs(resolveOutputs: (panelId: string, component: string) => Record<string, any> | null): void {
    for (const { id, component } of this.allPanels) {
      const outputs = resolveOutputs(id, component);
      if (outputs) {
        this.wireOutputs(id, outputs);
      }
    }
  }

  clear(): void {
    this._api?.clear();
    this._renderers.clear();
    this._homeGroup.clear();
  }

  layout(): void {
    // Dockview uses ResizeObserver by default, so this is a no-op
    // unless we've disabled auto-resizing
  }

  // --- Helpers ---

  /** Save the full dockview layout JSON to localStorage */
  private _persistLayout(): void {
    if (!this._api || !this._layoutStorageKey || this._suppressPersist) return;
    try {
      localStorage.setItem(this._layoutStorageKey, JSON.stringify(this._api.toJSON()));
    } catch (e) {
      console.warn('[DockviewWindowManager] Failed to persist layout:', e);
    }
  }

  private _mapPosition(pos: PanelPosition): any | null {
    if (pos.referencePanel) {
      return {
        referencePanel: pos.referencePanel,
        direction: pos.direction || 'within',
      };
    }
    if (pos.referenceGroup) {
      return {
        referenceGroup: pos.referenceGroup,
        direction: pos.direction || 'within',
      };
    }
    if (pos.direction) {
      return { direction: pos.direction };
    }
    return null;
  }

  private _isSessionWidget(component: string): boolean {
    return component === 'ChatWidget' || component === 'SessionWidget' || component === 'HeartbeatWidget';
  }

  /** Panels whose DOM must survive being hidden (see addPanel). */
  private _keepsDomWhenHidden(component: string): boolean {
    return this._isSessionWidget(component) || component === 'ExternalAppWidget';
  }

  private static readonly _KIND_BY_COMPONENT: Record<string, PanelKind> = {
    ChatWidget: 'session', SessionWidget: 'session', HeartbeatWidget: 'session',
    MarkdownWidget: 'document', MonacoWidget: 'document', DataViewerWidget: 'document', PreviewWidget: 'document',
    TerminalWidget: 'terminal',
    ExternalAppWidget: 'app',
  };

  private _kindOf(d: { kind?: PanelKind; component?: string } | undefined): PanelKind {
    return d?.kind || DockviewWindowManager._KIND_BY_COMPONENT[d?.component || ''] || 'tool';
  }

  /** After a layout restore, the last group holding each kind becomes its home. */
  private _seedHomeGroups(): void {
    if (!this._api) return;
    this._homeGroup.clear();
    for (const panel of this._api.panels) {
      if (panel.group) this._homeGroup.set(this._kindOf(panel.params as any), panel.group.id);
    }
  }

  /**
   * Hide tab headers when there's only a single group with a single tab
   * (nothing to tab-switch or drag). When multiple groups exist, always
   * show headers so users can drag tabs between groups.
   */
  private _updateSingleTabHeaders(): void {
    if (!this._api) return;
    const groups = this._api.groups;
    const multipleGroups = groups.length > 1;
    for (const group of groups) {
      group.header.hidden = !multipleGroups && group.panels.length <= 1;
    }
  }
}
